import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ProgressBar, Typography, Loading } from "../../components/common";
import {
  ArrowLeft,
  BookOpen,
  Clock,
  CheckCircle,
  Download,
} from "lucide-react";
import {
  useDownloadCourseResourceMutation,
  useGetMasterCourseByIdQuery,
} from "../../store/api/masterCourseApi";
import { LevelCard } from "./LevelCard";
import SuggestionBoardImage from "../../images/SuggestionBoardImage";
import { normalizePublicImageUrl } from "../../utils/imageUrl";
import { splitCourseTitle } from "../../utils/courseTitle";
import {
  formatRemainingMinutesLeft,
  formatTimeLeft,
} from "../../utils/progressUtils";
import { useGetCurrentUserQuery } from "../../store/api/postgresUserApi";

const MasterCourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: currentUser } = useGetCurrentUserQuery();
  const {
    data: masterCourse,
    isLoading,
    error,
  } = useGetMasterCourseByIdQuery(id!, { skip: !id });
  const [downloadCourseResource, { isLoading: isDownloadingResource }] =
    useDownloadCourseResourceMutation();
  const [resourceDownloadError, setResourceDownloadError] = React.useState<
    string | null
  >(null);

  const handleResourceDownload = async (
    downloadUrl: string,
    fileName: string,
  ) => {
    setResourceDownloadError(null);
    try {
      const blob = await downloadCourseResource(downloadUrl).unwrap();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setResourceDownloadError(
        "The resource could not be downloaded. Please try again.",
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] bg-background">
        <Loading />
      </div>
    );
  }

  if (error || !masterCourse) {
    const isForbidden = error && "status" in error && error.status === 403;
    const errorData =
      error && "data" in error && typeof error.data === "object"
        ? (error.data as { error_code?: string })
        : null;
    const isRetired = errorData?.error_code === "course_retired";
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Typography variant="h4" className="text-gray-600">
          {isRetired
            ? "This course has been retired"
            : isForbidden
              ? "Course not available on your plan"
              : "Course not found"}
        </Typography>
        {isRetired ? (
          <Typography
            variant="p"
            className="text-gray-500 text-center max-w-md"
          >
            Your completed progress is preserved. Any certificates remain
            available in Certificates; eligible certified credentials remain in
            your Passport.
          </Typography>
        ) : isForbidden ? (
          <Typography
            variant="p"
            className="text-gray-500 text-center max-w-md"
          >
            This course requires a higher plan tier. Contact your administrator
            to upgrade.
          </Typography>
        ) : null}
        <button
          onClick={() => navigate("/")}
          className="text-primary hover:underline"
        >
          Return to Home
        </button>
      </div>
    );
  }

  // Calculate remaining time from incomplete levels only
  const remainingMinutes = masterCourse.levels.reduce((sum, level) => {
    if (level.status === "locked_by_plan" || level.isCompleted) {
      return sum;
    }
    const levelMinutes = level.estimatedDurationMinutes || 0;
    const levelRemaining = Math.round(
      levelMinutes * (1 - level.progressPercentage / 100),
    );
    return sum + levelRemaining;
  }, 0);

  // Check if we have valid duration data
  const hasDurationData = masterCourse.levels.some(
    (level) =>
      level.status !== "locked_by_plan" && level.estimatedDurationMinutes > 0,
  );

  const timeDisplay =
    hasDurationData && !masterCourse.isCompleted
      ? formatRemainingMinutesLeft(remainingMinutes)
      : formatTimeLeft(
        masterCourse.progressPercentage,
        0,
        masterCourse.isCompleted,
      );
  const iconUrl = normalizePublicImageUrl(masterCourse.iconUri);
  const { primaryTitle, attribution } = splitCourseTitle(masterCourse.title);
  const resources = masterCourse.resources ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-2 text-gray-600 hover:text-primary transition w-fit"
      >
        <ArrowLeft size={20} />
        <span className="text-sm font-medium">Back to Home</span>
      </button>

      {/* Header section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Icon */}
          <div className="w-[180px] h-[150px] flex-shrink-0 overflow-hidden rounded-lg">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={masterCourse.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <SuggestionBoardImage />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              {masterCourse.isCompleted && (
                <div className="flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full text-sm">
                  <CheckCircle size={14} />
                  <span>Completed</span>
                </div>
              )}
            </div>

            <Typography variant="h2" weight="bold" className="text-gray-900">
              {primaryTitle}
            </Typography>

            {attribution && (
              <Typography variant="p" className="text-gray-500">
                {attribution}
              </Typography>
            )}

            {masterCourse.description && (
              <Typography variant="p" className="text-gray-600">
                {masterCourse.description}
              </Typography>
            )}

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-6 mt-2">
              <div className="flex items-center gap-2 text-gray-600">
                <BookOpen size={18} />
                <span className="text-sm">
                  {masterCourse.completedLevels} of {masterCourse.totalLevels}{" "}
                  modules complete
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={18} />
                <span className="text-sm">{timeDisplay}</span>
              </div>
            </div>

            {/* Overall progress */}
            <div className="mt-2 max-w-md">
              <ProgressBar progress={masterCourse.progressPercentage} />
            </div>
          </div>
        </div>
      </div>

      {resources.length > 0 && (
        <section
          aria-label="Course resources"
          className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200"
        >
          <Typography variant="h4" weight="bold" className="text-gray-900">
            Course resources
          </Typography>
          <div className="mt-4 flex flex-col gap-3">
            {resources.map((resource) => (
              <button
                key={resource.key}
                type="button"
                disabled={isDownloadingResource}
                onClick={() =>
                  handleResourceDownload(
                    resource.downloadUrl,
                    resource.fileName,
                  )
                }
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 p-4 text-left hover:border-primary hover:bg-slate-50 transition disabled:cursor-wait disabled:opacity-60"
              >
                <span>
                  <span className="block font-semibold text-gray-900">
                    {resource.title}
                  </span>
                  <span className="block text-sm text-gray-600 mt-1">
                    {resource.description}
                  </span>
                </span>
                <Download
                  aria-hidden="true"
                  className="shrink-0 text-primary"
                  size={22}
                />
              </button>
            ))}
            {resourceDownloadError && (
              <p role="alert" className="text-sm text-red-700">
                {resourceDownloadError}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Modules section */}
      <div className="flex flex-col gap-4">
        <Typography variant="h4" weight="bold" className="text-gray-900">
          Course Modules
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...masterCourse.levels]
            .sort((a, b) => a.levelNumber - b.levelNumber)
            .map((level) => (
              <LevelCard
                key={level.id}
                level={level}
                masterCourseId={masterCourse.id}
                isTrialOrg={currentUser?.is_trial_org === true}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default MasterCourseDetail;
